import type { ChangeEvent } from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import type { ExpressShippingFieldsProps } from './types';

const numberValue = (event: ChangeEvent<HTMLInputElement>): number => Number(event.target.value);

const ExpressShippingFields = ({ countryCode, value, errors, onChange }: ExpressShippingFieldsProps) => {
  const updateDuration = (field: 'minDay' | 'maxDay', days: number) => {
    onChange({
      ...value,
      shippingDuration: {
        ...value.shippingDuration,
        [field]: days,
      },
    });
  };

  return (
    <div className="border rounded p-3 mt-3">
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h6 className="mb-1">Express Shipping</h6>
          <div className="text-muted small">Fee and threshold use the {countryCode} policy currency.</div>
        </div>
        <Form.Check
          type="switch"
          id={`express-shipping-enabled-${countryCode}`}
          label="Enable Express Shipping"
          checked={value.enabled}
          onChange={(event) => onChange({ ...value, enabled: event.target.checked })}
        />
      </div>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3" controlId={`express-shipping-fee-${countryCode}`}>
            <Form.Label>Express Shipping Fee *</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={value.shippingFee}
              disabled={!value.enabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                onChange({ ...value, shippingFee: numberValue(event) })
              }
              isInvalid={Boolean(errors.shippingFee)}
            />
            <Form.Control.Feedback type="invalid">{errors.shippingFee}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3" controlId={`express-free-shipping-threshold-${countryCode}`}>
            <Form.Label>Express Free Shipping Threshold *</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={value.freeShippingThreshold}
              disabled={!value.enabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                onChange({ ...value, freeShippingThreshold: numberValue(event) })
              }
              isInvalid={Boolean(errors.freeShippingThreshold)}
            />
            <Form.Control.Feedback type="invalid">{errors.freeShippingThreshold}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3" controlId={`express-min-days-${countryCode}`}>
            <Form.Label>Express Minimum Delivery Days *</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={value.shippingDuration.minDay}
              disabled={!value.enabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateDuration('minDay', numberValue(event))}
              isInvalid={Boolean(errors.minDay)}
            />
            <Form.Control.Feedback type="invalid">{errors.minDay}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3" controlId={`express-max-days-${countryCode}`}>
            <Form.Label>Express Maximum Delivery Days *</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={value.shippingDuration.maxDay}
              disabled={!value.enabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateDuration('maxDay', numberValue(event))}
              isInvalid={Boolean(errors.maxDay)}
            />
            <Form.Control.Feedback type="invalid">{errors.maxDay}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {!value.enabled && (
        <Alert variant="secondary" className="mb-0 py-2">
          Saved values are retained while Express Shipping is disabled.
        </Alert>
      )}
    </div>
  );
};

export default ExpressShippingFields;
